import { useState, useEffect, useMemo, useRef } from "react";
import { ValidateBillUseCase } from "../../domain/use-cases";
import { ValidationRepositoryImpl } from "../../data/repositories";
import {
  ValidationResponse,
  ValidationData,
} from "../../domain/entities/ValidationEntity";
import { BillEntity } from "../../domain/entities/BillEntity";
import {
  AI_DETECTION_MAX_RETRIES,
  AI_DETECTION_RETRY_BASE_DELAY_MS,
  sleep,
} from "../../../../shared/constants/aiDetectionMessages";

// Initialize the use case with repository
const validationRepository = new ValidationRepositoryImpl();
const validateBillUseCase = new ValidateBillUseCase(validationRepository);

interface UseBillValidationProps {
  billData: any;
  billDocument: BillEntity;
  onValidationComplete?: (result: ValidationResponse) => void;
}

export const useBillValidation = ({
  billData,
  billDocument,
  onValidationComplete,
}: UseBillValidationProps) => {
  const [validationData, setValidationData] = useState<ValidationData | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastValidatedDataRef = useRef<string | null>(null);
  const [isFirstValidation, setIsFirstValidation] = useState(true);

  // Cooldown: skip re-validation when billData changed only because our callback
  // updated the parent (tax_ids, TDS, etc.). Prevents double API call.
  const lastValidationCompletedAtRef = useRef<number>(0);
  const VALIDATION_COOLDOWN_MS = 1500;

  // Store the latest callback in a ref to avoid re-running effect when callback changes
  const onValidationCompleteRef = useRef(onValidationComplete);

  // Update the ref whenever the callback changes (but don't trigger effect re-run)
  useEffect(() => {
    onValidationCompleteRef.current = onValidationComplete;
  }, [onValidationComplete]);

  // Memoize the billData to prevent unnecessary re-renders
  const memoizedBillData = useMemo(() => {
    return billData ? JSON.stringify(billData) : null;
  }, [billData]);

  // Run validation when component mounts or billData actually changes
  useEffect(() => {
    let cancelled = false;

    const now = Date.now();
    const isInCooldown =
      now - lastValidationCompletedAtRef.current < VALIDATION_COOLDOWN_MS;

    // Only show loading if we have new data to validate AND we're not in cooldown.
    // In cooldown, billData changed because our callback updated the parent (tax/TDS);
    // don't flash "Running AI Detection..." — keep showing the existing results.
    if (
      memoizedBillData &&
      memoizedBillData !== lastValidatedDataRef.current &&
      !isInCooldown
    ) {
      setLoading(true);
      setError(null);
    }
    if (
      memoizedBillData &&
      memoizedBillData !== lastValidatedDataRef.current &&
      isInCooldown
    ) {
      lastValidatedDataRef.current = memoizedBillData;
    }

    const runValidation = async () => {
      if (
        !memoizedBillData ||
        memoizedBillData === lastValidatedDataRef.current
      ) {
        setLoading(false);
        return;
      }

      // If we just completed a validation, the parent likely updated billJson
      // from our callback (tax_ids, TDS). Don't run the API again.
      const nowInRun = Date.now();
      if (
        nowInRun - lastValidationCompletedAtRef.current <
        VALIDATION_COOLDOWN_MS
      ) {
        lastValidatedDataRef.current = memoizedBillData;
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      const validationType = isFirstValidation
        ? "full_validation"
        : "normal_validation";
      let attempt = 0;
      let lastError: string | null = null;

      while (attempt <= AI_DETECTION_MAX_RETRIES) {
        if (cancelled) return;

        attempt++;
        try {
          const response = await validateBillUseCase.execute(
            JSON.parse(memoizedBillData),
            billDocument,
            validationType,
          );

          if (response.success) {
            // Merge validation results instead of replacing, so that keys only
            // returned during full_validation (tds_classification, gl_classification)
            // are preserved across subsequent normal_validation calls.
            setValidationData((prev) => {
              if (!prev) return response.data;
              return {
                ...prev,
                ...response.data,
                validation_results: {
                  ...prev.validation_results,
                  ...response.data.validation_results,
                },
              };
            });
            lastValidatedDataRef.current = memoizedBillData;
            lastValidationCompletedAtRef.current = Date.now();
            onValidationCompleteRef.current?.(response);
            if (isFirstValidation) {
              setIsFirstValidation(false);
            }
            setLoading(false);
            return;
          }

          lastError = "Validation failed";
        } catch (err) {
          lastError = "Error running validation";
          console.error(
            `Validation error (attempt ${attempt}/${AI_DETECTION_MAX_RETRIES + 1}):`,
            err,
          );
        }

        if (attempt <= AI_DETECTION_MAX_RETRIES) {
          await sleep(AI_DETECTION_RETRY_BASE_DELAY_MS * attempt);
        }
      }

      if (!cancelled) {
        setError(lastError);
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(runValidation, 500);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [memoizedBillData, billDocument, isFirstValidation]);

  // Reset when billDocument changes (user navigates to different document)
  useEffect(() => {
    setIsFirstValidation(true);
    lastValidationCompletedAtRef.current = 0;
    lastValidatedDataRef.current = null;
    setError(null);
  }, [billDocument?.jobid]);

  return {
    validationData,
    loading,
    error,
  };
};
